import React from "react";
import { Bookmark } from "lucide-react";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { TokenIcon } from "@/components/TokenIcon";
import { Sparkline } from "@/components/ui/Sparkline";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import { AlertTagBadges, ExchangeStack, Pill } from "@/components/alerts/AlertBits";
import { useSavedAlerts } from "@/hooks/useSavedAlerts";
import {
  changeTextClass,
  formatAlertPrice,
  formatChange,
  formatTimeAgo,
  useAlertCardData,
} from "@/hooks/useAlertCardData";
import { exchangeBrand, marketTypeLabel } from "@/lib/exchange-brand";

interface CryptoAlertTileProps {
  alert: CryptoAlertData;
  onViewPositions?: (alert: CryptoAlertData) => void;
  onDetailsClick?: () => void;
  onTrade?: (alert: CryptoAlertData) => void;
}

const Stat: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex min-w-0 flex-col items-center justify-center gap-1 px-1.5 text-center">
    <p className="flex max-w-full items-center gap-1 truncate text-[11px] leading-4 text-[#a3a3a3]">
      {label}
    </p>
    {children}
  </div>
);

const valueClass = "max-w-full truncate text-[15px] font-semibold leading-5";

/** Alert card, mobile layout of the redesign (Figma "Alerts → Mobile" card). */
export const CryptoAlertTile: React.FC<CryptoAlertTileProps> = ({
  alert,
  onViewPositions,
  onDetailsClick,
  onTrade,
}) => {
  const isListing = alert.type === "new_coin";
  const d = useAlertCardData(alert);
  const { isSaved, toggle } = useSavedAlerts();
  const saved = isSaved(alert.id);
  const source = exchangeBrand(alert.exchange);
  const closed = d.bestPosition?.status === "CLOSED";

  const thirdLabel = d.bestPosition
    ? closed
      ? `${d.positionLabel} closed at`
      : `${d.positionLabel} open`
    : "Change";

  return (
    <div ref={d.ref} className="glass flex flex-col gap-4 p-4">
      {/* Token, name, announcing exchange; bookmark on the right */}
      <div className="flex items-center gap-3">
        <TokenIcon
          symbol={alert.currency}
          logo={alert.tokenMetadata?.logo}
          size="lg"
          className="!h-12 !w-12 shrink-0"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 items-baseline gap-2">
            <p className="shrink-0 text-base font-semibold leading-5 text-white">{alert.currency}</p>
            {alert.tokenMetadata?.name && (
              <p className="truncate text-xs leading-4 text-[#a3a3a3]">{alert.tokenMetadata.name}</p>
            )}
          </div>
          <div className="flex min-w-0 items-center gap-1.5 text-[11px] leading-4 text-[#a3a3a3]">
            <ExchangeLogo code={alert.exchange} size={16} />
            <span className="truncate">
              {isListing ? "" : "Delisted · "}
              {source.name} {marketTypeLabel(alert.exchangeType)} · {formatTimeAgo(alert.createdAt, true)}
            </span>
          </div>
        </div>
        <button
          type="button"
          aria-label={saved ? "Remove bookmark" : "Bookmark alert"}
          aria-pressed={saved}
          onClick={(e) => {
            e.stopPropagation();
            toggle(alert.id);
          }}
          className="-mr-1 -mt-1 self-start p-1.5"
        >
          <Bookmark
            className={`size-[18px] ${saved ? "fill-[#00c27e] text-[#00c27e]" : "text-[#737373]"}`}
          />
        </button>
      </div>

      {/* Badges on the left, venues that listed the token at alert time on the right */}
      {(d.isNew || d.tags.length > 0 || !isListing || alert.hasAutotraded || d.tradableExchanges.length > 0) && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <AlertTagBadges
              isNew={d.isNew}
              isListing={isListing}
              tags={d.tags}
              textClass="text-[10px] leading-[14px]"
              gapClass="gap-1.5"
            />
            {alert.hasAutotraded && (
              <Pill tone="brand" className="text-[10px] leading-[14px]">
                Autotraded 🤖
              </Pill>
            )}
          </div>
          <div
            className="flex shrink-0 items-center gap-1.5"
            title={
              d.tradableExchanges.length
                ? `Tradable at ${isListing ? "listing" : "delisting"} on ${d.tradableExchanges
                    .map((code) => exchangeBrand(code).name)
                    .join(", ")}`
                : `Not tradable on any exchange at ${isListing ? "listing" : "delisting"}`
            }
          >
            <ExchangeStack codes={d.tradableExchanges} size={20} />
          </div>
        </div>
      )}

      <div className="h-px w-full bg-white/10" />

      {/* Prices: three equal columns, centred, so they line up with or without the chart */}
      {d.allPriceDataUnavailable ? (
        <p className="py-1 text-center text-sm text-[#a3a3a3]">Not available for trading yet</p>
      ) : (
        <div className="grid grid-cols-3 items-stretch divide-x divide-white/10">
          <Stat label={d.isClosedPosition ? `${d.positionLabel} opened at` : "Alert Price"}>
            <p className={`${valueClass} text-white`}>
              {formatAlertPrice(
                d.isClosedPosition ? (d.bestPosition?.entryPrice ?? null) : alert.price
              )}
            </p>
          </Stat>
          <Stat
            label={
              <>
                Current Price
                {d.isSubscribed && d.livePrice && (
                  <span className="size-1.5 shrink-0 rounded-full bg-green-500" aria-label="Live" />
                )}
              </>
            }
          >
            {d.isPriceLoading ? (
              <span className="h-5 w-16 animate-pulse rounded bg-gray-600/30" />
            ) : (
              <p className={`${valueClass} text-white`}>{formatAlertPrice(d.livePrice)}</p>
            )}
          </Stat>
          <Stat label={thirdLabel}>
            {d.bestPosition ? (
              <>
                <p className={`${valueClass} text-white`}>
                  {formatAlertPrice(
                    closed ? (d.bestPosition.exitPrice ?? null) : d.bestPosition.entryPrice
                  )}
                </p>
                {closed && d.bestPosition.pnlPercentage != null && (
                  <p className={`text-[11px] leading-4 ${changeTextClass(d.bestPosition.pnlPercentage)}`}>
                    PnL: {d.bestPosition.pnlPercentage.toFixed(2)}%
                  </p>
                )}
                {!closed && d.priceChangeToPosition !== null && (
                  <p className={`text-[11px] leading-4 ${changeTextClass(d.priceChangeToPosition)}`}>
                    {formatChange(d.priceChangeToPosition)}
                  </p>
                )}
              </>
            ) : d.isPriceLoading ? (
              <span className="h-5 w-12 animate-pulse rounded bg-gray-600/30" />
            ) : (
              <p
                className={`${valueClass} ${
                  d.priceChange === null ? "text-[#737373]" : changeTextClass(d.priceChange)
                }`}
              >
                {d.priceChange === null ? "–" : formatChange(d.priceChange)}
              </p>
            )}
            {d.sparkline.length > 1 && (
              <Sparkline values={d.sparkline} width={72} height={22} className="mt-0.5" />
            )}
          </Stat>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDetailsClick?.();
          }}
          className="flex h-10 flex-1 items-center justify-center rounded-full border border-[#404040] bg-[#171717] px-4 text-sm font-medium text-white hover:bg-[#262626]"
        >
          Details
        </button>
        {!d.allPriceDataUnavailable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (d.positions.length > 0 && onViewPositions) onViewPositions(alert);
              else if (d.positions.length === 0 && onTrade) onTrade(alert);
            }}
            className="flex h-10 flex-1 items-center justify-center rounded-full border-y border-r border-[#8ee7b7] bg-accent px-4 text-sm font-medium text-white hover:bg-accent-hover"
          >
            {d.positions.length === 0
              ? "Trade"
              : d.positions.length > 1
                ? `View positions (${d.positions.length})`
                : "View positions"}
          </button>
        )}
      </div>
    </div>
  );
};
